import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Radio,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { PortalPodcastEpisodePayload } from '../../portal/podcastEpisodeEnvelope';
import { fetchCompanyProfile } from '../../api/companyProfile';
import {
  DEFAULT_ARTICLE_CATEGORIES,
  normalizeArticleCategory,
} from '../../constants/articleEditorConstants';
import { DraftHeroBanner } from './DraftHeroBanner';
import { ArticleBodyEditorJs } from './ArticleBodyEditorJs';
import { LibraryLinkedContentRail } from './LibraryLinkedContentRail';
import type { ContrastingView, FrontendNotebook } from '../../contexts/NotebooksContext';
import type { NewsCluster, WikiConcept } from '../../types/types';
import { plainTextStats } from '../../utils/draftTextStats';
import { analyzeDraftSentiment, type DraftSentiment } from '../../utils/draftSentiment';

const METADATA_CONTROL_HEIGHT_PX = 36;

function contrastingViewIsExpressed(v: ContrastingView) {
  return String(v.point_of_view_type || '').toLowerCase() === 'expressed';
}

function ContrastingViewRow({ view }: { view: ContrastingView }) {
  const expressed = contrastingViewIsExpressed(view);
  return (
    <Card sx={{ bgcolor: '#0a0a0a', border: '1px solid #27272a', mb: 1 }}>
      <CardContent
        sx={{
          py: 1,
          px: 1.25,
          '&:last-child': { pb: 1 },
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 12,
            height: 12,
            mt: 0.35,
            borderRadius: '50%',
            flexShrink: 0,
            border: '1.5px solid #71717a',
            bgcolor: expressed ? '#bbf7d0' : 'transparent',
            boxSizing: 'border-box',
          }}
        />
        <Typography variant="body2" sx={{ color: '#d4d4d8', fontSize: '0.8rem' }}>
          {view.point_of_view_description}
        </Typography>
      </CardContent>
    </Card>
  );
}

export type PodcastPortalDraftPanelProps = {
  episode: PortalPodcastEpisodePayload;
  onEpisodeChange: (next: PortalPodcastEpisodePayload) => void;
  bodyText: string;
  onBodyChange: (text: string) => void;
  readOnly?: boolean;
  /** Library drawer: linked notebooks, clusters, insights (article-aligned). */
  linkRail?: {
    podcastId: string;
    linkedNotebookIds: string[];
    allNotebooks: FrontendNotebook[];
    activeNotebooks: FrontendNotebook[];
    podcastClusterIds: string[];
    onLinkedNotebookIdsChange: (ids: string[]) => Promise<void>;
    clusters: NewsCluster[];
    clustersLoading?: boolean;
    clusterIds?: string[];
    onOpenClusterDetails: (event: React.MouseEvent, cluster: NewsCluster) => void;
    isClusterContentGenerating: (clusterTitle: string, scopeId?: string) => boolean;
    /** Scope id for cluster-content jobs (e.g. linked article notebook id). */
    clusterContentScopeId?: string | null;
    selectedClusterTitleForEpisode: string | null;
    onSelectClusterTitleForEpisode: (title: string | null) => void;
    /** Opens generate dialog (duration, language, visibility) then calls API. */
    onRequestGeneratePodcastEpisode: () => void;
    isGeneratingPodcastEpisode: boolean;
    contrastingViews?: ContrastingView[] | null;
    onAddClustersToDraft: (clusters: NewsCluster[]) => Promise<boolean>;
    onRemoveClusterFromDraft: (clusterTitle: string) => Promise<boolean>;
    draftLinkedWikiEntities?: WikiConcept[];
    onDraftLinkedWikiEntitiesChange?: (next: WikiConcept[]) => Promise<void>;
  };
};

export function PodcastPortalDraftPanel({
  episode,
  onEpisodeChange,
  bodyText,
  onBodyChange,
  readOnly = false,
  linkRail,
}: PodcastPortalDraftPanelProps) {
  const episodeRef = useRef(episode);
  episodeRef.current = episode;
  const onBodyChangeRef = useRef(onBodyChange);
  onBodyChangeRef.current = onBodyChange;
  const onEpisodeChangeRef = useRef(onEpisodeChange);
  onEpisodeChangeRef.current = onEpisodeChange;
  const [articleCategories, setArticleCategories] = useState<string[]>(() => [...DEFAULT_ARTICLE_CATEGORIES]);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [railTab, setRailTab] = useState(0);
  const [insightSubTab, setInsightSubTab] = useState(0);
  const [contrastingPovTab, setContrastingPovTab] = useState(0);

  useEffect(() => {
    if (!linkRail && railTab > 1) setRailTab(0);
  }, [linkRail, railTab]);

  useEffect(() => {
    if (insightSubTab !== 1) setContrastingPovTab(0);
  }, [insightSubTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cp = await fetchCompanyProfile();
      if (cancelled || !cp) return;
      const cats = (cp.article_categories ?? [])
        .map((s) => String(s).trim())
        .filter((s) => Boolean(s));
      if (cats.length) setArticleCategories(cats);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedCategory = useMemo(
    () => normalizeArticleCategory(episode.category || episode.vertical, articleCategories),
    [episode.category, episode.vertical, articleCategories]
  );

  const stats = useMemo(() => plainTextStats(bodyText), [bodyText]);

  const [sentiment, setSentiment] = useState<DraftSentiment>(() => ({
    label: 'Neutral / mixed',
    summary: 'Analyzing draft…',
    positiveHits: 0,
    negativeHits: 0,
  }));

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void analyzeDraftSentiment(bodyText).then((next) => {
        if (!cancelled) setSentiment(next);
      });
    }, 160);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [bodyText]);

  const sentimentFaceRows = useMemo(
    () => [
      { emoji: '😊', active: sentiment.label.startsWith('Leans positive') },
      { emoji: '😐', active: sentiment.label.includes('Neutral') },
      { emoji: '😟', active: sentiment.label.startsWith('Leans negative') },
    ],
    [sentiment.label]
  );

  const contrastingViews = linkRail?.contrastingViews;
  const hasContrasting = Boolean(contrastingViews?.length);
  const contrastingExpressed = useMemo(
    () => (contrastingViews || []).filter((v) => contrastingViewIsExpressed(v)),
    [contrastingViews]
  );
  const contrastingSuggested = useMemo(
    () =>
      (contrastingViews || []).filter((v) => String(v.point_of_view_type || '').toLowerCase() === 'suggested'),
    [contrastingViews]
  );

  const thumb = (episode.thumbnail_image || episode.image || '').trim();
  const banner = (episode.banner_image || '').trim();

  const pushEpisode = (patch: Partial<PortalPodcastEpisodePayload>) => {
    if (readOnly) return;
    onEpisodeChange({ ...episode, ...patch });
  };

  const clustersLoading = linkRail?.clustersLoading ?? false;
  const clusterIds = linkRail?.clusterIds;
  const scopeId = linkRail?.clusterContentScopeId ?? undefined;

  /** Direct podcast clusters plus clusters inherited from linked library drafts (same idea as article). */
  const insightClusters = useMemo(() => {
    if (!linkRail) return [];
    const byTitle = new Map<string, NewsCluster>();
    for (const c of linkRail.clusters) {
      byTitle.set(c.title, c);
    }
    for (const lid of linkRail.linkedNotebookIds) {
      const nb = linkRail.allNotebooks.find((n) => n.id === lid);
      if (!nb?.clusters?.length) continue;
      for (const cl of nb.clusters) {
        if (byTitle.has(cl.title)) continue;
        byTitle.set(cl.title, {
          ...cl,
          articles: cl.articles || [],
          suggested_titles: cl.suggested_titles || [],
          social_media: cl.social_media || [],
          contrasting_views: cl.contrasting_views || [],
          wiki_concepts: cl.wiki_concepts || [],
          timeline: cl.timeline || [],
        });
      }
    }
    return Array.from(byTitle.values());
  }, [linkRail]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 0.5,
          borderBottom: '1px solid #27272a',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 600, mr: 0.5 }}>
          Metadata
        </Typography>
        <Chip size="small" label={resolvedCategory} sx={{ bgcolor: '#27272a', color: '#e4e4e7', flexShrink: 0 }} />
        <Button
          size="small"
          endIcon={
            <ExpandMoreIcon
              sx={{ transition: '0.2s', transform: metadataOpen ? 'rotate(180deg)' : 'none', color: '#a1a1aa' }}
            />
          }
          onClick={() => setMetadataOpen((v) => !v)}
          sx={{ color: '#60a5fa', textTransform: 'none', py: 0, minWidth: 'auto', ml: { xs: 0, sm: 'auto' } }}
        >
          {metadataOpen ? 'Hide' : 'Show'} images & category
        </Button>
      </Box>

      <Collapse in={metadataOpen}>
        <Box
          sx={{
            mt: 1,
            mb: 1.5,
            p: 1.5,
            border: '1px solid #27272a',
            borderRadius: 1,
            bgcolor: '#171717',
          }}
        >
          <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block', mb: 1.5 }}>
            Thumbnail and banner use the same URL / upload flow as event drafts.
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#fafafa', mb: 0.75 }}>
            Thumbnail image
          </Typography>
          <DraftHeroBanner
            imageUrl={thumb}
            onImageUrlCommitted={(url) =>
              pushEpisode({ thumbnail_image: url, image: url || episode.image })
            }
            ariaLabel="Podcast thumbnail"
            maxHeight={140}
            readOnly={readOnly}
          />
          <Typography variant="subtitle2" sx={{ color: '#fafafa', mb: 0.75, mt: 2 }}>
            Banner image
          </Typography>
          <DraftHeroBanner
            imageUrl={banner}
            onImageUrlCommitted={(url) => pushEpisode({ banner_image: url })}
            ariaLabel="Podcast banner"
            maxHeight={200}
            readOnly={readOnly}
          />

          <FormControl fullWidth size="small" sx={{ mt: 2 }} required>
            <InputLabel id="podcast-cat-label" sx={{ color: '#a1a1aa' }}>
              Category
            </InputLabel>
            <Select
              labelId="podcast-cat-label"
              label="Category"
              disabled={readOnly}
              value={resolvedCategory}
              onChange={(e: SelectChangeEvent) =>
                pushEpisode({ category: e.target.value, vertical: e.target.value })
              }
              sx={{
                color: '#fafafa',
                minHeight: METADATA_CONTROL_HEIGHT_PX,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3f3f46' },
              }}
            >
              {articleCategories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Collapse>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          mt: 1,
        }}
      >
        <Box sx={{ flex: { xs: 1, md: '0 0 60%' }, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" sx={{ color: '#a1a1aa', mb: 0.5 }}>
            Show
          </Typography>
          <ArticleBodyEditorJs
            notebookId={episode.id || 'podcast'}
            readOnly={!!readOnly}
            seed={{ content: bodyText, content_blocks: episode.content_blocks || [] }}
            onBlurValidation={() => {}}
            onBodyChange={(content, content_blocks) => {
              onBodyChangeRef.current(content);
              onEpisodeChangeRef.current({
                ...episodeRef.current,
                show: content,
                script: content,
                content_blocks,
              });
            }}
          />
        </Box>

        <Box
          sx={{
            flex: { xs: 1, md: '0 0 40%' },
            minWidth: { xs: '100%', md: 200 },
            minHeight: 0,
            overflow: 'hidden',
            borderLeft: { md: '1px solid #27272a' },
            pl: { md: 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tabs
            value={railTab}
            onChange={(_, v) => setRailTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              alignSelf: 'stretch',
              width: '100%',
              minHeight: 40,
              '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
              '& .MuiTab-root': { color: '#a1a1aa', minHeight: 40, py: 0.5, fontSize: '0.75rem' },
              '& .Mui-selected': { color: '#fafafa' },
              '& .MuiTabs-indicator': { backgroundColor: '#60a5fa' },
            }}
          >
            <Tab label="Feedback" />
            <Tab label="Insights" />
            {linkRail ? <Tab label="Linked content" /> : null}
          </Tabs>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              pt: 1.5,
              pr: 0.5,
              ...(linkRail && railTab === 2
                ? { overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                : { overflow: 'auto' }),
            }}
          >
            {railTab === 0 && (
              <Box>
                <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 0.5 }}>
                  Words: <strong>{stats.words}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 0.5 }}>
                  Characters: <strong>{stats.characters}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 1.5 }}>
                  Est. reading time: <strong>{stats.readingTimeMinutes} min</strong> (230 wpm)
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    rowGap: 0.5,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#fafafa' }}>
                    Sentiment
                  </Typography>
                  <Chip label={sentiment.label} size="small" sx={{ bgcolor: '#27272a', color: '#e4e4e7' }} />
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 0.5 } }}>
                    {sentimentFaceRows.map((row) => (
                      <Box
                        key={row.emoji}
                        component="span"
                        sx={{
                          fontSize: '1.2rem',
                          lineHeight: 1,
                          cursor: 'default',
                          opacity: row.active ? 1 : 0.38,
                          filter: row.active ? 'none' : 'grayscale(35%)',
                        }}
                      >
                        {row.emoji}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
            {linkRail && railTab === 1 && (
              <Box>
                <Tabs
                  value={insightSubTab}
                  onChange={(_, v) => setInsightSubTab(v)}
                  sx={{
                    alignSelf: 'stretch',
                    width: '100%',
                    minHeight: 36,
                    mb: 1,
                    '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
                    '& .MuiTab-root': { color: '#a1a1aa', minHeight: 36, fontSize: '0.7rem' },
                    '& .Mui-selected': { color: '#fafafa' },
                    '& .MuiTabs-indicator': { backgroundColor: '#a78bfa' },
                  }}
                >
                  <Tab label="Related clusters" />
                  <Tab label="Contrasting views" />
                </Tabs>
                {insightSubTab === 0 && (
                  <Box>
                    {clustersLoading && (
                      <Box display="flex" alignItems="center" gap={1} py={1}>
                        <CircularProgress size={22} sx={{ color: '#60a5fa' }} />
                        <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                          Loading…
                        </Typography>
                      </Box>
                    )}
                    {!clustersLoading && insightClusters.length > 0 && (
                      <>
                        <List dense disablePadding sx={{ mb: 1.5 }}>
                          {insightClusters.map((cluster, index) => (
                            <ListItem
                              key={cluster.title}
                              button
                              onClick={(e) => linkRail.onOpenClusterDetails(e, cluster)}
                              divider={index < insightClusters.length - 1}
                              sx={{
                                bgcolor: '#0a0a0a',
                                mb: 1,
                                borderRadius: 1,
                                border: '1px solid #27272a',
                                '&:hover': { bgcolor: '#1a1a1a' },
                                pr: linkRail.isClusterContentGenerating(cluster.title, scopeId) ? '132px' : '108px',
                                boxSizing: 'border-box',
                              }}
                            >
                              <ListItemText
                                primary={cluster.title}
                                secondary={`${(cluster.general_overview || '').substring(0, 80)}…`}
                                primaryTypographyProps={{
                                  sx: {
                                    color: '#fafafa',
                                    fontSize: '0.85rem',
                                    pr: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  },
                                }}
                                secondaryTypographyProps={{
                                  sx: {
                                    color: '#a1a1aa',
                                    fontSize: '0.7rem',
                                    pr: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  },
                                }}
                              />
                              <ListItemSecondaryAction>
                                {linkRail.isClusterContentGenerating(cluster.title, scopeId) && (
                                  <CircularProgress size={18} sx={{ color: '#60a5fa', mr: 0.5 }} />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={(e) => linkRail.onOpenClusterDetails(e, cluster)}
                                  sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <Radio
                                  size="small"
                                  checked={linkRail.selectedClusterTitleForEpisode === cluster.title}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() =>
                                    linkRail.onSelectClusterTitleForEpisode(
                                      linkRail.selectedClusterTitleForEpisode === cluster.title
                                        ? null
                                        : cluster.title
                                    )
                                  }
                                  value={cluster.title}
                                  sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#60a5fa' } }}
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() => linkRail.onRequestGeneratePodcastEpisode()}
                          disabled={
                            !linkRail.selectedClusterTitleForEpisode ||
                            linkRail.isGeneratingPodcastEpisode ||
                            readOnly
                          }
                          sx={{
                            bgcolor: '#60a5fa',
                            color: '#0a0a0a',
                            '&:hover': { bgcolor: '#3b82f6' },
                            '&.Mui-disabled': { bgcolor: '#27272a', color: '#52525b' },
                          }}
                        >
                          {linkRail.isGeneratingPodcastEpisode ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <CircularProgress size={16} sx={{ color: '#0a0a0a' }} />
                              Generating…
                            </Box>
                          ) : (
                            'Generate episode'
                          )}
                        </Button>
                      </>
                    )}
                    {!clustersLoading && insightClusters.length === 0 && (
                      <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                        {clusterIds && clusterIds.length > 0
                          ? 'No cluster data loaded.'
                          : 'No associated clusters to this piece of content.'}
                      </Typography>
                    )}
                  </Box>
                )}
                {insightSubTab === 1 && (
                  <Box>
                    {!hasContrasting ? (
                      <Typography variant="body2" sx={{ color: '#71717a' }}>
                        No contrasting views on attached clusters yet. Generate cluster content from a linked article
                        draft that includes this cluster, or add clusters in Linked content.
                      </Typography>
                    ) : (
                      <>
                        <Tabs
                          value={contrastingPovTab}
                          onChange={(_, v) => setContrastingPovTab(v)}
                          sx={{
                            alignSelf: 'stretch',
                            width: '100%',
                            minHeight: 36,
                            mb: 1,
                            '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
                            '& .MuiTab-root': { color: '#a1a1aa', minHeight: 36, fontSize: '0.75rem' },
                            '& .Mui-selected': { color: '#fafafa' },
                            '& .MuiTabs-indicator': { backgroundColor: '#34d399' },
                          }}
                        >
                          <Tab label="Expressed" />
                          <Tab label="Suggested" />
                        </Tabs>
                        {contrastingPovTab === 0 &&
                          (contrastingExpressed.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#71717a' }}>
                              No perspectives marked as expressed.
                            </Typography>
                          ) : (
                            contrastingExpressed.map((view, index) => (
                              <ContrastingViewRow key={`e-${index}`} view={view} />
                            ))
                          ))}
                        {contrastingPovTab === 1 &&
                          (contrastingSuggested.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#71717a' }}>
                              No suggested perspectives yet.
                            </Typography>
                          ) : (
                            contrastingSuggested.map((view, index) => (
                              <ContrastingViewRow key={`s-${index}`} view={view} />
                            ))
                          ))}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {!linkRail && railTab === 1 && (
              <Typography variant="body2" sx={{ color: '#71717a' }}>
                Open this episode from the Library to manage clusters and linked content.
              </Typography>
            )}
            {linkRail && railTab === 2 && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <LibraryLinkedContentRail
                  mode="podcast"
                  subjectId={linkRail.podcastId}
                  readOnly={readOnly}
                  linkedNotebookIds={linkRail.linkedNotebookIds}
                  onLinkedNotebookIdsChange={linkRail.onLinkedNotebookIdsChange}
                  allNotebooks={linkRail.allNotebooks}
                  activeNotebooks={linkRail.activeNotebooks}
                  linkedClusters={linkRail.clusters}
                  onOpenClusterDetails={linkRail.onOpenClusterDetails}
                  onRemoveCluster={async (title) => linkRail.onRemoveClusterFromDraft(title)}
                  podcastClusterIds={linkRail.podcastClusterIds}
                  draftLinkedWikiEntities={linkRail.draftLinkedWikiEntities ?? episode.linked_wiki_entities ?? []}
                  onDraftLinkedWikiEntitiesChange={
                    linkRail.onDraftLinkedWikiEntitiesChange ??
                    (async (next) => {
                      pushEpisode({ linked_wiki_entities: next });
                    })
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
